import { __assign, __makeTemplateObject, __read, __rest, __spread, __values } from "tslib";
import MuiTabs from '@material-ui/core/Tabs';
import React, { cloneElement, forwardRef, isValidElement, useEffect, useRef, useState, } from 'react';
import { getResizeObserver, styled, useForkRef, useMountState, useOnReRender, useThrottle, useRcPortalWindowContext, } from '../../../../foundation';
import { DEFAULT_MORE_MENU_TAB_LABEL, MoreMenuTab, } from '../MoreMenuTab';
import { MoreMenuTabSentinel } from './MoreMenuTabSentinel';
// with of more button with a icon
var basicMoreButtonSize = 44;
var getTabsInfoFromChildren = function (children) {
    var childrenInfo = React.Children.map(children, function (child, index) {
        var _a, _b;
        if (isValidElement(child)) {
            var key = (_a = child.key) !== null && _a !== void 0 ? _a : index;
            return {
                index: index,
                key: key,
                value: (_b = child.props.value) !== null && _b !== void 0 ? _b : key,
                element: child,
            };
        }
        return null;
    });
    if (!childrenInfo)
        return [];
    return childrenInfo.filter(function (info) { return Boolean(info); });
};
var _MoreMenuTabs = forwardRef(function (props, ref) {
    var orientation = props.orientation, childrenProp = props.children, value = props.value, onChange = props.onChange, _a = props.MoreButtonProps, MoreButtonProps = _a === void 0 ? {} : _a, _b = props.resizeThrottleTime, resizeThrottleTime = _b === void 0 ? 100 : _b, rest = __rest(props, ["orientation", "children", "value", "onChange", "MoreButtonProps", "resizeThrottleTime"]);
    var onGroupInfoChange = MoreButtonProps.onGroupInfoChange, MoreButtonPropsRest = __rest(MoreButtonProps, ["onGroupInfoChange"]);
    var isVertical = orientation === 'vertical';
    var sizeKey = isVertical ? 'offsetHeight' : 'offsetWidth';
    var innerRef = useRef(null);
    var moreTabRef = useRef(null);
    var sentinelStartRef = useRef(null);
    var tabsRef = useForkRef(innerRef, ref);
    var _c = useRcPortalWindowContext().externalWindow, externalWindow = _c === void 0 ? window : _c;
    var mountStateRef = useMountState();
    var _d = __read(useState(function () {
        var tabsInfo = getTabsInfoFromChildren(childrenProp);
        if (!tabsInfo) {
            return {
                all: [],
                tab: [],
                menu: [],
            };
        }
        return {
            all: tabsInfo,
            tab: tabsInfo,
            menu: [],
        };
    }), 2), groupInfo = _d[0], setGroupInfo = _d[1];
    var throttleCalculateGroupInfo = useThrottle(function () {
        var e_1, _a;
        var _b;
        var tablist = (_b = innerRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('[role="tablist"]');
        if (!tablist)
            return;
        var allTabs = Array.from(tablist.children);
        var allTabsSize = [];
        var selectedIndex = groupInfo.all.findIndex(function (info) { return info.value === value; });
        var tablistSize = tablist[sizeKey];
        var moreButtonSize = basicMoreButtonSize;
        var allTabsSizeSumWidthoutMoreButton = 0;
        try {
            for (var allTabs_1 = __values(allTabs), allTabs_1_1 = allTabs_1.next(); !allTabs_1_1.done; allTabs_1_1 = allTabs_1.next()) {
                var tabEl = allTabs_1_1.value;
                if (typeof tabEl.dataset['tabOriginIndex'] === 'string') {
                    var tabOriginIndex = Number(tabEl.dataset['tabOriginIndex']);
                    var elSize = tabEl[sizeKey];
                    allTabsSize[tabOriginIndex] = elSize;
                    allTabsSizeSumWidthoutMoreButton += elSize;
                }
                else if (typeof tabEl.dataset['tabMoreButton'] === 'string') {
                    var elSize = tabEl[sizeKey];
                    moreButtonSize = elSize;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (allTabs_1_1 && !allTabs_1_1.done && (_a = allTabs_1.return)) _a.call(allTabs_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        var newGroupInfo = {
            all: groupInfo.all,
            tab: [],
            menu: [],
        };
        if (allTabsSizeSumWidthoutMoreButton > tablistSize) {
            var allTabsSizeSum = allTabsSizeSumWidthoutMoreButton + moreButtonSize;
            var targetSize = allTabsSizeSum;
            for (var i = allTabsSize.length - 1; i >= 0; i--) {
                if (i !== selectedIndex) {
                    targetSize -= allTabsSize[i];
                    newGroupInfo.menu.unshift(groupInfo.all[i]);
                    if (targetSize <= tablistSize) {
                        break;
                    }
                }
            }
            newGroupInfo.tab = groupInfo.all.filter(function (info) { return !newGroupInfo.menu.some(function (_a) {
                var index = _a.index;
                return index === info.index;
            }); });
        }
        else {
            newGroupInfo.tab = newGroupInfo.all;
        }
        var shouldUpdateGroupInfo = false;
        if (newGroupInfo.tab.length === groupInfo.tab.length &&
            newGroupInfo.tab.length === groupInfo.tab.length) {
            for (var i = 0; i < groupInfo.tab.length; i++) {
                if (newGroupInfo.tab[i] !== groupInfo.tab[i]) {
                    shouldUpdateGroupInfo = true;
                    break;
                }
            }
        }
        else {
            shouldUpdateGroupInfo = true;
        }
        if (shouldUpdateGroupInfo) {
            // prevent resize observer loop limit exceeded
            setTimeout(function () {
                if (mountStateRef.current)
                    setGroupInfo(newGroupInfo);
            }, 0);
        }
    }, resizeThrottleTime);
    useEffect(function () {
        var e_2, _a;
        var _b;
        var tablist = (_b = innerRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('[role="tablist"]');
        if (!tablist)
            return;
        var resizeObserver = getResizeObserver(throttleCalculateGroupInfo, externalWindow);
        resizeObserver.observe(tablist);
        try {
            for (var _c = __values(Array.from(tablist.children)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var tabItem = _d.value;
                resizeObserver.observe(tabItem, 
                // TODO: remove this, after remove ResizeObserver polyfill
                // @ts-ignore
                { box: 'border-box' });
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_2) throw e_2.error; }
        }
        var mutationObserver = new externalWindow.MutationObserver(function (mutations) {
            var e_3, _a;
            try {
                for (var mutations_1 = __values(mutations), mutations_1_1 = mutations_1.next(); !mutations_1_1.done; mutations_1_1 = mutations_1.next()) {
                    var mutation = mutations_1_1.value;
                    if (mutation.type === 'childList') {
                        mutation.removedNodes.forEach(function (removedNode) {
                            if (removedNode.getAttribute('role') === 'tab') {
                                resizeObserver.unobserve(removedNode);
                            }
                        });
                        mutation.addedNodes.forEach(function (addedNode) {
                            if (addedNode.getAttribute('role') === 'tab') {
                                resizeObserver.observe(addedNode, 
                                // TODO: remove this, after remove ResizeObserver polyfill
                                // @ts-ignore
                                { box: 'border-box' });
                            }
                        });
                        // should re-calculate group info when dom is removed and not added
                        if (mutation.removedNodes.length && !mutation.addedNodes.length) {
                            setTimeout(function () {
                                if (mountStateRef.current)
                                    throttleCalculateGroupInfo();
                            }, 0);
                        }
                    }
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (mutations_1_1 && !mutations_1_1.done && (_a = mutations_1.return)) _a.call(mutations_1);
                }
                finally { if (e_3) throw e_3.error; }
            }
        });
        mutationObserver.observe(tablist, {
            childList: true,
        });
        return function () {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // No need calculate during the first rendering,
    // resize observer will do this
    useOnReRender(function () {
        throttleCalculateGroupInfo();
    }, [value], false);
    useOnReRender(function () {
        var tabsInfo = getTabsInfoFromChildren(childrenProp);
        var newGroupInfo = {
            all: tabsInfo,
            tab: [],
            menu: [],
        };
        setGroupInfo(function (preGroupInfo) {
            var e_4, _a;
            var _loop_1 = function (tabInfo) {
                if (preGroupInfo.tab.some(function (_a) {
                    var key = _a.key;
                    return tabInfo.key === key;
                })) {
                    newGroupInfo.tab.push(tabInfo);
                }
                else {
                    // push new tab or invisible old tab to menu group
                    newGroupInfo.menu.push(tabInfo);
                }
            };
            try {
                // Prevent moreButton from flickering after children update
                for (var tabsInfo_1 = __values(tabsInfo), tabsInfo_1_1 = tabsInfo_1.next(); !tabsInfo_1_1.done; tabsInfo_1_1 = tabsInfo_1.next()) {
                    var tabInfo = tabsInfo_1_1.value;
                    _loop_1(tabInfo);
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (tabsInfo_1_1 && !tabsInfo_1_1.done && (_a = tabsInfo_1.return)) _a.call(tabsInfo_1);
                }
                finally { if (e_4) throw e_4.error; }
            }
            return newGroupInfo;
        });
    }, [childrenProp], false);
    useEffect(function () {
        onGroupInfoChange === null || onGroupInfoChange === void 0 ? void 0 : onGroupInfoChange([
            groupInfo.tab.map(function (_a) {
                var index = _a.index, key = _a.key;
                return ({ index: index, key: key });
            }),
            groupInfo.menu.map(function (_a) {
                var index = _a.index, key = _a.key;
                return ({ index: index, key: key });
            }),
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupInfo]); // ignore onGroupInfoChange
    var useMoreMode = groupInfo.menu.length > 0;
    var MoreMenuTabCmp = (function () {
        if (!useMoreMode)
            return null;
        var menuItems = groupInfo.menu.map(function (_a) {
            var key = _a.key, element = _a.element;
            return __assign(__assign({}, element.props), { key: key });
        });
        return useMoreMode ? (React.createElement(MoreMenuTab, __assign({}, MoreButtonPropsRest, { key: DEFAULT_MORE_MENU_TAB_LABEL, size: rest.size, menuItems: menuItems, ref: moreTabRef, onChange: onChange, orientation: orientation }))) : null;
    })();
    var children = (function () {
        var tabGroupElements = groupInfo.tab.map(function (_a) {
            var index = _a.index, key = _a.key, tabValue = _a.value, element = _a.element;
            return cloneElement(element, {
                key: key,
                value: tabValue,
                'data-tab-origin-index': index,
                'data-tab-key': key,
            });
        });
        if (!useMoreMode)
            return tabGroupElements;
        return __spread(tabGroupElements, [
            React.createElement(MoreMenuTabSentinel, { id: "start", value: "sentinel-start", key: "sentinel-start", ref: sentinelStartRef, onFocus: function () {
                    var _a;
                    (_a = moreTabRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                } })
        ], groupInfo.menu.map(function (_a) {
            var index = _a.index, key = _a.key, tabValue = _a.value, element = _a.element;
            return cloneElement(element, {
                key: key,
                value: tabValue,
                'data-tab-origin-index': index,
                'data-tab-key': key,
                'aria-hidden': true,
                disabled: true,
                style: __assign(__assign({}, element.props.style), { opacity: 0, position: 'absolute', top: 0, left: 0 }),
            });
        }), [
            React.createElement(MoreMenuTabSentinel, { id: "end", value: "sentinel-end", key: "sentinel-end", onFocus: function () {
                    var _a;
                    var focusTarget = ((_a = sentinelStartRef.current) === null || _a === void 0 ? void 0 : _a.previousElementSibling) ||
                        moreTabRef.current;
                    if (focusTarget)
                        focusTarget.focus();
                } }),
        ]);
    })();
    return (React.createElement(MuiTabs, __assign({}, rest, { ref: tabsRef, value: value, variant: "standard", indicatorColor: "primary", textColor: "primary", onChange: onChange, orientation: orientation }),
        children,
        MoreMenuTabCmp));
});
/** inner component */
var MoreMenuTabs = styled(_MoreMenuTabs)(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
MoreMenuTabs.displayName = 'MoreMenuTabs';
export { MoreMenuTabs };
var templateObject_1;
